/**
 * JSON-safety helpers for self-service payloads (publish / save / cache).
 *
 * Some rendered components mutate the data objects they're handed with
 * back-references — notably `DrillDownComponent.flattenTree`, which sets
 * `node.parent` on every tree node. Those objects are the SAME ones stored on a
 * tile's widget data, so serializing the dashboard layout (JSON.stringify, in
 * both the publish POST and the localStorage save) hits a circular structure
 * (`children → parent → children …`) and throws. `toSerializable` returns a deep
 * clone that drops `parent` and cuts any remaining cycle, leaving the structure
 * otherwise intact. It also strips functions (e.g. an echarts tooltip/label
 * `formatter` callback baked into a saved chart `option`) — JSON.stringify
 * silently drops them anyway, but `structuredClone` (used to snapshot a layout
 * before editing) throws on them instead, so callers that need a clone safe for
 * BOTH should go through here rather than `structuredClone` directly.
 */

/** Keys that are known runtime back-references — always dropped when cloning. */
const BACK_REF_KEYS = new Set(['parent']);

/** Deep-clone `value` into a JSON-safe structure: strips known back-reference
 *  keys and breaks any true cycle (an object referencing one of its ancestors).
 *  Shared but non-cyclic references are preserved as separate copies. */
export function toSerializable<T>(value: T): T {
  const clone = (v: unknown, ancestors: Set<unknown>): unknown => {
    if (typeof v === 'function') return undefined;
    if (v === null || typeof v !== 'object') return v;
    if (ancestors.has(v)) return undefined; // ancestor cycle — cut it
    const nextAncestors = new Set(ancestors).add(v);
    if (Array.isArray(v)) return v.map((item) => clone(item, nextAncestors));
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(v as Record<string, unknown>)) {
      if (BACK_REF_KEYS.has(key)) continue;
      out[key] = clone((v as Record<string, unknown>)[key], nextAncestors);
    }
    return out;
  };
  return clone(value, new Set()) as T;
}
